import { Box, Center, Flex } from '@chakra-ui/layout';
import {
    Button,
    Checkbox,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { Spinner } from '@chakra-ui/spinner';
import { useKeycloak } from '@react-keycloak/web';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import './App.css';
import { Navbar } from './components/Navbar';
import { Sidebar } from './components/Sidebar';
import config from './config';
import ProfileContext, {
    License,
    ProfileContextProvider,
    ProfileError,
} from './context/ProfileContext';
import { SelectedGameContextProvider } from './context/SelectedGameContext';
import { useLocalStorage } from './hooks/useLocalStorage';
import { AddCSharpActorDepot } from './pages/AddCSharpActorDepot';
import { AddInventory } from './pages/AddInventory';
import { AddItem } from './pages/AddItem';
import { AddLeaderboardConfig } from './pages/AddLeaderboardConfig';
import { AddProduct } from './pages/AddProduct';
import { AddRMBDepot } from './pages/AddRMBDepot';
import { AddUnityActorDepot } from './pages/AddUnityActorDepot';
import { ActorAssets } from './pages/CSharpActor';
import { CreateGame } from './pages/CreateGame';
import { RMBAssets } from './pages/GolangActor';
import { Whitelist } from './pages/Whitelist';
import { Home } from './pages/Home';
import { InventoryManagement } from './pages/InventoryManagement';
import { LeaderboardsConfigs } from './pages/LeaderboardConfigs';
import { Overview } from './pages/Overview';
import { Owners } from './pages/Owners';
import { RunningGames } from './pages/RunningGames';
import { UpdateGame } from './pages/UpdateGame';
import { Welcome } from './pages/Welcome';
import { Timeout } from './util/types';

const CSharpActorSubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="add-csharp-actor-depot" element={<AddCSharpActorDepot />} />
            <Route path="add-unity-actor-depot" element={<AddUnityActorDepot />} />
            <Route path="*" element={<ActorAssets />} />
        </Routes>
    );
};

const GolangActorSubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="add-rmb-depot" element={<AddRMBDepot />} />
            <Route path="*" element={<RMBAssets />} />
        </Routes>
    );
};

const WhitelistSubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="*" element={<Whitelist />} />
        </Routes>
    );
};

const InventorySubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="edit-product/:id" element={<AddProduct isSaved={false} />} />
            <Route path="edit-product/:id" element={<AddProduct isSaved={false} />} />
            <Route path="add-item" element={<AddItem isSaved={false} />} />
            <Route path="add-inventory" element={<AddInventory isSaved={false} />} />
            <Route path="edit-inventory/:id" element={<AddInventory isSaved={true} />} />
            <Route path="*" element={<InventoryManagement />} />
        </Routes>
    );
};

const LeaderboardSubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route
                path="add-leaderboard-config"
                element={<AddLeaderboardConfig isSaved={false} />}
            />
            <Route
                path="edit-leaderboard-config/:id"
                element={<AddLeaderboardConfig isSaved={true} />}
            />
            <Route path="*" element={<LeaderboardsConfigs />} />
        </Routes>
    );
};

const SessionsSubRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="*" element={<RunningGames />} />
        </Routes>
    );
};

const GameSubRouterBase: React.FC<{}> = () => {
    const { key } = useParams();
    const profile = useContext(ProfileContext);

    return (
        <SelectedGameContextProvider currLicense={profile.licenses[key || '']}>
            <Flex flex={1}>
                <Sidebar></Sidebar>
                <Box padding={5} width="100%">
                    <Routes>
                        <Route path="overview" element={<Overview />} />
                        {config.features.leaderboards && (
                            <Route path="leaderboards/*" element={<LeaderboardSubRouterBase />} />
                        )}
                        {config.features.inventory && (
                            <Route path="inventory/*" element={<InventorySubRouterBase />} />
                        )}
                        <Route path="csharp-actor/*" element={<CSharpActorSubRouterBase />} />
                        <Route path="golang-actor/*" element={<GolangActorSubRouterBase />} />
                        <Route path="whitelist/*" element={<WhitelistSubRouterBase />} />
                        {config.features.runningGames && (
                            <Route path="sessions/*" element={<SessionsSubRouterBase />} />
                        )}
                        <Route path="owners/*" element={<Owners />} />
                        <Route path="settings/*" element={<UpdateGame />} />
                        <Route path="*" element={<></>} />
                    </Routes>
                </Box>
            </Flex>
        </SelectedGameContextProvider>
    );
};

const AuthorizedRouterBase: React.FC<{}> = () => {
    const [licenses, setLicenses] = useState({});
    // TODO(DEVPOR21-43) put the license loading logic somewhere else to keep things clean, maybe a hook
    const [licensesLoaded, setLicensesLoaded] = useState(false);
    const [errors, setErrors] = useState<ProfileError[]>([]);
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const areLicensesLoaded = useRef(false);
    const controller = new AbortController();
    const signal = controller.signal;
    const timeoutRef = useRef<Timeout>();

    useEffect(() => {
        areLicensesLoaded.current = licensesLoaded;
    }, [licensesLoaded]);

    const cancelCall = () => {
        if (!areLicensesLoaded.current) {
            console.log('Request aborted.');
            controller.abort();
            return toast({
                title: 'Request Timed Out',
                description: "We're sorry! please try again later.",
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };
    useEffect(() => {
        timeoutRef.current = setTimeout(cancelCall, 30000);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const getLicensesFromToken = async () => {
        try {
            const response = await fetch(`${config.licenseServiceURL}/user/license`, {
                signal: signal,
                headers: {
                    Authorization: `Bearer ${keycloak.token}`,
                },
            });
            if (response.status !== 200) {
                if (response.status === 403) {
                    setErrors((errors) => errors.concat(ProfileError.NO_PERMISSION));
                } else {
                    setErrors((errors) => errors.concat(ProfileError.UNKNOWN_ERROR));
                }
                return;
            }
            const json = await response.json();
            const licensesArr = json.data;
            setLicenses(
                licensesArr.reduce((curr: any, license: License) => {
                    curr[license.key] = license;
                    return curr;
                }, {}),
            );
            setLicensesLoaded(true);
        } catch (error) {
            if (signal.aborted) {
                setErrors((errors) => errors.concat(ProfileError.REQUEST_ABORTED));
            }
        }
    };

    useEffect(() => {
        setErrors([]);
    }, []);

    useEffect(() => {
        getLicensesFromToken();
    }, []);

    return (
        <ProfileContextProvider
            licenses={licenses}
            licensesLoaded={licensesLoaded}
            errors={errors}
            getLicensesFromToken={getLicensesFromToken}
        >
            <Navbar></Navbar>
            <Flex flex={1}>
                <Routes>
                    <Route path="/game/:key/*" element={<GameSubRouterBase />} />
                    <Route path="/create" element={<CreateGame />} />
                    <Route path="/" element={<Home />} />
                </Routes>
            </Flex>
        </ProfileContextProvider>
    );
};

const UnauthorizedRouterBase: React.FC<{}> = () => {
    return (
        <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );
};

function App() {
    const { keycloak, initialized } = useKeycloak();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [hideDisclaimer, setHideDisclaimer] = useLocalStorage('hide-disclaimer', false);
    const [hideDisclaimerChecked, setHideDisclaimerChecked] = useState(false);

    useEffect(() => {
        if (initialized && keycloak.authenticated && !hideDisclaimer) onOpen();
    }, [initialized]);

    const onDisclaimerClosed = () => {
        if (hideDisclaimerChecked) {
            setHideDisclaimer(true);
        }
        onClose();
    };

    return (
        <>
            <Router>
                {initialized ? (
                    keycloak.authenticated ? (
                        <AuthorizedRouterBase />
                    ) : (
                        <UnauthorizedRouterBase />
                    )
                ) : (
                    <Center width="100%" height="100%">
                        <Spinner data-cy="loading" />
                    </Center>
                )}
            </Router>
            <Modal isOpen={isOpen} onClose={onDisclaimerClosed}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Welcome to the Developer Portal</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <p>
                            This product is in early stages, so there may be some issues we haven't
                            gotten around to working on yet. Please let us know on our Discord
                            server if you are experiencing any issues. Thanks! :)
                        </p>
                        <br />
                        <Checkbox
                            isChecked={hideDisclaimerChecked}
                            onChange={(e) => setHideDisclaimerChecked(e.target.checked)}
                        >
                            Don't show this message again
                        </Checkbox>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onDisclaimerClosed}>Ok</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

export default App;
